'use client';

import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Database,
  ArrowUpDown,
  Users,
  CheckCircle2,
  TrendingUp,
  ArrowDown,
  ArrowUp,
  ShieldCheck,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import EventCard from '@/components/event/EventCard';
import { useSearchParams, useRouter } from 'next/navigation';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import SearchBar from '@/components/SearchBar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ListFilterSidebar from '@/components/filters/ListFilterSidebar';
import MobileFilterDrawer from '@/components/filters/MobileFilterDrawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useUser } from '@clerk/nextjs';
import { useListStore } from '@/stores/useListStore';
import { useUrlSync } from '@/hooks/useUrlSync';

const ITEMS_PER_PAGE = 12;

const sortOptions = [
  { value: 'date_desc', label: 'Latest', Icon: ArrowDown },
  { value: 'date_asc', label: 'Oldest', Icon: ArrowUp },
  { value: 'price_asc', label: 'Price: Low-High', Icon: ArrowUp },
  { value: 'price_desc', label: 'Price: High-Low', Icon: ArrowDown },
  { value: 'leads_desc', label: 'Most Leads', Icon: Users },
  { value: 'sold_desc', label: 'Best Selling', Icon: CheckCircle2 },
  { value: 'trending', label: 'Trending', Icon: TrendingUp },
];

export default function ListsPageClient() {
  // Get values and actions from Zustand store
  const {
    currentPage,
    sortBy,
    searchTerm,
    category,
    priceRange,
    leadsRange,
    containsEmails,
    containsVerifiedEmails,
    containsPhones,
    lastUpdatedDays,
    setCurrentPage,
    setSortBy,
  } = useListStore();

  // Sync URL with store state
  useUrlSync();

  const router = useRouter();
  const { user } = useUser();

  // Local UI state (not filter related)
  const [modalOpen, setModalOpen] = useState(false);
  const [requestDescription, setRequestDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Custom list request mutation
  const createCustomListRequest = useMutation(api.customListRequests.create);

  // Query for events with advanced filters from store
  const result = useQuery(api.events.getWithAdvancedFilters, {
    searchTerm,
    category,
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    sortBy,
    priceMin: priceRange.min,
    priceMax: priceRange.max,
    leadsMin: leadsRange.min,
    leadsMax: leadsRange.max,
    containsEmails,
    containsVerifiedEmails,
    containsPhones,
    lastUpdatedDays,
  });

  // Handle page change with rate limiting
  const handlePageChange = useCallback(
    (newPage: number) => {
      // Prevent excessive pagination
      const lastPageChange = sessionStorage.getItem('lastPageChange');
      const now = Date.now();
      if (lastPageChange && now - parseInt(lastPageChange, 10) < 1000) {
        return; // Prevent rapid page changes
      }

      sessionStorage.setItem('lastPageChange', now.toString());
      setCurrentPage(newPage);
    },
    [setCurrentPage],
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (value: string) => {
      setSortBy(value);
    },
    [setSortBy],
  );

  // Handler for submitting the custom list request
  const handleCustomListRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setSubmitError('You must be logged in to request a custom list');
      return;
    }

    if (requestDescription.length < 10) {
      setSubmitError('Please provide a more detailed description');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createCustomListRequest({ description: requestDescription });
      setSubmitSuccess(true);
      setRequestDescription('');
    } catch (error) {
      setSubmitError('Failed to submit request. Please try again later.');
      console.error('Error submitting custom list request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if any filters are active for UI display
  const hasActiveFilters = useMemo(() => {
    return !!(
      searchTerm ||
      category ||
      priceRange.min !== undefined ||
      priceRange.max !== undefined ||
      leadsRange.min !== undefined ||
      leadsRange.max !== undefined ||
      containsEmails ||
      containsVerifiedEmails ||
      containsPhones ||
      lastUpdatedDays !== undefined ||
      (sortBy && sortBy !== 'date_desc')
    );
  }, [
    searchTerm,
    category,
    priceRange.min,
    priceRange.max,
    leadsRange.min,
    leadsRange.max,
    containsEmails,
    containsVerifiedEmails,
    containsPhones,
    lastUpdatedDays,
    sortBy,
  ]);

  return (
    <div className="min-h-screen bg-background">
      {/* Custom List Request Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request a Custom Lead List</DialogTitle>
            <DialogDescription>
              Tell us what kind of leads you're looking for and we'll create a custom list for you.
            </DialogDescription>
          </DialogHeader>

          {submitSuccess ? (
            <div className="py-6 text-center">
              <ShieldCheck className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Request Submitted!</h3>
              <p className="text-muted-foreground mb-4">
                We've received your custom list request and will get back to you soon.
              </p>
              <DialogClose asChild>
                <Button variant="outline" className="w-full">
                  Close
                </Button>
              </DialogClose>
            </div>
          ) : (
            <form onSubmit={handleCustomListRequest} className="space-y-4">
              <Textarea
                value={requestDescription}
                onChange={(e) => setRequestDescription(e.target.value)}
                placeholder="Describe your ideal lead list, target industry, location, data fields, etc."
                minLength={10}
                maxLength={1000}
                required
                className="min-h-[100px]"
                disabled={isSubmitting}
              />
              {submitError && <div className="text-red-600 text-sm">{submitError}</div>}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={isSubmitting || requestDescription.length < 10}
                  className="w-full"
                >
                  {isSubmitting ? <LoadingSpinner /> : 'Submit Request'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <div className="container px-4 py-6 sm:py-8 lg:py-10 max-w-screen-xl mx-auto">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {/* Desktop Filter Sidebar - Hidden on Mobile */}
          <div className="hidden md:block w-full md:w-64 lg:w-72 shrink-0">
            <ListFilterSidebar className="sticky top-24" />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Search and Category Area */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <SearchBar />
              </div>

              {/* New container for Sort and Filter buttons */}
              <div className="flex flex-row gap-2 w-full sm:w-auto items-center">
                <div className="flex-1">
                  {' '}
                  {/* Sort Dropdown takes available space */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-between gap-1.5" // Changed to justify-between
                      >
                        <span>
                          {' '}
                          {/* Text wrapped in span for better flex control */}
                          {sortOptions.find((option) => option.value === sortBy)?.label ||
                            'Sort By'}
                        </span>
                        <ArrowUpDown className="h-4 w-4" /> {/* Icon moved after text */}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      {sortOptions.map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className="flex items-center gap-2"
                        >
                          <option.Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                          {sortBy === option.value && <Check className="h-4 w-4 ml-auto" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex-shrink-0">
                  {' '}
                  {/* Container for MobileFilterDrawer */}
                  <MobileFilterDrawer />
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  <span className="text-muted-foreground">Verified B2B Lead Lists</span>
                </h1>
                {hasActiveFilters && (
                  <div className="text-sm text-muted-foreground mt-1">Filtered results</div>
                )}
              </div>
            </div>

            {/* Loading State */}
            {!result ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : result.events.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {result.events.map((event) => (
                  <EventCard key={event._id} eventId={event._id} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-16 bg-card rounded-xl border border-border">
                <Database className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-base sm:text-lg lg:text-xl font-medium text-foreground mb-2">
                  No matching lead lists found
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4">
                  Try broadening your search criteria or using different categories
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-2">
                  <Button
                    variant="outline"
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 w-full sm:w-auto"
                  >
                    Request a Custom List
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => router.push('/marketplace')}
                    className="flex items-center gap-2 w-full sm:w-auto"
                  >
                    Check Scrapers Marketplace
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {result && (
          <div className="flex flex-col items-center justify-center gap-3 mt-8 mb-4">
            {/* Total count display */}
            {result.totalCount > 0 && (
              <div className="text-center mb-2">
                <span className="text-base font-medium">
                  {result.totalCount.toLocaleString()}{' '}
                  <span className="text-muted-foreground">
                    {result.totalCount === 1 ? 'List' : 'Lists'}
                  </span>
                </span>
              </div>
            )}

            {/* Pagination controls */}
            {result.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {result.totalPages}
                  </span>
                </div>

                <Button
                  variant="outline"
                  onClick={() => handlePageChange(Math.min(result.totalPages, currentPage + 1))}
                  disabled={currentPage === result.totalPages}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
